import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Grid, Image, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { convertGoogleDriveUrl, isVideoUrl } from '@/lib/urlUtils';
import Lightbox from '@/components/Lightbox';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_843f7c3a-3692-4728-b14f-ba431557a9c8/artifacts/qxen4yga_Ray%20Creators%20-%20Logo.png";

const GalleryPage = () => {
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const categoriesRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const categoriesData = await publicApi.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const projectsData = await publicApi.getProjects(params);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollCategories = (direction) => {
    if (categoriesRef.current) {
      const scrollAmount = 200;
      categoriesRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const stats = [
    { icon: Grid, label: 'Projects', value: projects.length },
    { icon: Image, label: 'Categories', value: categories.length },
    { icon: Users, label: 'Happy Clients', value: '50+' },
  ];

  return (
    <div className="min-h-screen pb-20" data-testid="gallery-page">
      {/* Instagram-style Profile Header */}
      <section className="py-12 border-b border-border" data-testid="gallery-header">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Profile Image */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative"
            >
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-primary p-1">
                <img
                  src={LOGO_URL}
                  alt="Ray Creators"
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
            </motion.div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  <span className="font-serif uppercase tracking-wider">Ray</span>
                  <span className="font-script text-primary text-4xl md:text-5xl ml-1">Creators</span>
                </h1>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Creative Design Studio | Branding | Packaging | Social Media Graphics | 
                  Crafting visual stories that inspire.
                </p>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex justify-center md:justify-start gap-8"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center" data-testid={`stat-${stat.label.toLowerCase()}`}>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Highlights - Instagram Stories Style */}
      <section className="py-6 border-b border-border sticky top-[73px] bg-background z-40" data-testid="categories-section">
        <div className="container mx-auto px-6">
          <div className="relative">
            {/* Scroll Buttons */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 rounded-full shadow-md hidden md:flex"
              onClick={() => scrollCategories('left')}
              data-testid="categories-scroll-left"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 rounded-full shadow-md hidden md:flex"
              onClick={() => scrollCategories('right')}
              data-testid="categories-scroll-right"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>

            {/* Categories */}
            <div
              ref={categoriesRef}
              className="flex gap-6 overflow-x-auto category-scroll px-4 md:px-12 py-2"
            >
              {/* All Category */}
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex flex-col items-center gap-2 flex-shrink-0"
                data-testid="category-all"
              >
                <div className={`category-circle flex items-center justify-center bg-muted ${!selectedCategory ? 'active border-primary' : ''}`}>
                  <Grid className="h-6 w-6 text-muted-foreground" />
                </div>
                <span className={`text-xs font-medium ${!selectedCategory ? 'text-primary' : 'text-muted-foreground'}`}>
                  All
                </span>
              </button>

              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.name)}
                  className="flex flex-col items-center gap-2 flex-shrink-0"
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`category-circle ${selectedCategory === category.name ? 'active border-primary' : ''}`}>
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className={`text-xs font-medium text-center max-w-[80px] truncate ${selectedCategory === category.name ? 'text-primary' : 'text-muted-foreground'}`}>
                    {category.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid - Instagram Style */}
      <section className="py-6" data-testid="projects-section">
        <div className="container mx-auto px-0 md:px-6">
          {loading ? (
            <div className="gallery-grid">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse" />
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="gallery-grid">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="gallery-item cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                  data-testid={`project-${index}`}
                >
                  <div className="gallery-item-inner">
                  {isVideoUrl(project.thumbnail) ? (
                    <video
                      src={convertGoogleDriveUrl(project.thumbnail)}
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={convertGoogleDriveUrl(project.thumbnail)}
                      alt={project.title}
                    />
                  )}
                  <div className="overlay">
                    <div className="text-white text-center">
                      <Image className="h-6 w-6 mx-auto" />
                      {project.media?.length > 1 && (
                        <span className="text-sm">{project.media.length}</span>
                      )}
                    </div>
                  </div>
                  </div>
                  {/* Multiple images indicator */}
                  {project.media?.length > 1 && (
                    <div className="absolute top-2 right-2 bg-black/50 rounded p-1">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 4h10v10H4V4zm12-2v10h4v4h-4v4h4V10h4V4h-4V0h-4v2z"/>
                      </svg>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-muted-foreground">
              <Image className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No projects found</p>
              <p className="text-sm">Check back soon for new content!</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedProject && (
        <Lightbox
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
};

export default GalleryPage;
